import { useState, useMemo, Fragment } from 'react'
import { Card, Table, Tag, Title, Text, Icon } from '@nimbus-ds/components'
import { CopyCampaign } from '@/components/merchant/CopyCampaign'
import {
  ArrowsVerticalIcon,
  ArrowupIcon,
  ArrowDownIcon,
  ArrowsHorizontalIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@nimbus-ds/icons'

interface ChannelCell {
  sessions: number
  conversions: number
  sessions_with_origin: number
  /** Window revenue for the channel. Optional only because older callers hand-summed cells. */
  revenue?: number
  /** ga4-engagement-v1: sessions that did more than bounce. Native channel days only. */
  engaged?: number
  /** Saw a product or category. Secondary, never the yardstick — it's confounded by landing page. */
  shoppers?: number
  /**
   * The engagement DENOMINATOR, and the reason this column can be trusted. Engagement counters
   * ramped from their deploy date, so `engaged / sessions` understates the rate by the unmeasured
   * fraction — on GR's 90-day window that is 44.9% read as 20.9%. Only days that actually carried
   * the counters accumulate here. Absent → fall back to `sessions` (single-day / legacy callers).
   */
  engagement_sessions?: number
}

interface Coverage {
  sessions_total: number
  sessions_with_origin: number
}

interface TrafficSourceTableProps {
  // GA4-style channel rollup (one-per-session), window-aggregated. Keyed by GA4 channel
  // name + a reserved `unattributed` (orphaned conversions). Conversions are REAL, not
  // estimated — but per-channel CR is a LOWER BOUND (orphan bias relocates into rates).
  channels: Record<string, ChannelCell>
  // GA4 Default Channel Group session totals — reconciliation reference. null if no GA4.
  ga4ChannelTotals?: Record<string, { sessions: number }> | null
  // Store-level origin-capture coverage (window aggregate) for the panel chip.
  coverage?: Coverage | null
  // Per-channel campaign drill-down (P2), keyed by channel. Expands a channel row into
  // its campaigns (+ untagged/other). Absent/empty where no campaign data exists yet.
  breakdown?: Record<string, ChannelBreakdown> | null
}

/** A drill-down cell carries the same metric set the channel row does (reader `aggregateBreakdown`). */
interface BreakdownCell {
  sessions: number
  conversions: number
  revenue?: number
  engaged?: number
  shoppers?: number
}

interface ChannelBreakdown {
  sources: Record<string, BreakdownCell>
  campaigns: Array<BreakdownCell & { campaign: string; medium: string | null }>
  untagged: BreakdownCell
  other: BreakdownCell
}

interface ChannelRow {
  name: string
  channelKey: string
  sessions: number
  conversions: number
  conversionRate: number
  vsAverage: number
  coverage: number          // per-channel origin coverage (0..1)
  ga4Sessions: number | null
  revenue: number
  valuePerVisit: number
  /** 0..1, or null when this window measured no engagement for the channel. */
  engagementRate: number | null
  shopperRate: number | null
  engagementSessions: number
}

type SortKey =
  | 'sessions' | 'conversions' | 'conversionRate' | 'vsAverage'
  | 'revenue' | 'valuePerVisit' | 'engagementRate'
type SortDirection = 'asc' | 'desc'

const UNATTRIBUTED = 'unattributed'

/** Noise floor. Channels below it are hidden from the rows but declared in the footnote. */
const MIN_SESSIONS = 5

const fmtMoney = (n: number) =>
  n >= 1000
    ? `$${Math.round(n).toLocaleString('es-AR')}`
    : `$${n.toLocaleString('es-AR', { maximumFractionDigits: n >= 100 ? 0 : 2 })}`

const fmtRate = (r: number | null) => (r == null ? '—' : `${(r * 100).toFixed(1)}%`)

// Spanish labels for GA4 channel names — kept in sync with TrafficSourceTimeline's CHANNELS.
const CHANNEL_LABELS: Record<string, string> = {
  'Paid Search': 'Búsqueda pagada',
  'Paid Social': 'Social pagado',
  'Organic Search': 'Búsqueda orgánica',
  'Organic Social': 'Social orgánico',
  'Email': 'Email',
  'Referral': 'Referidos',
  'Paid Other': 'Otros pagados',
  'Direct': 'Directo',
}

export function TrafficSourceTable({ channels, ga4ChannelTotals, coverage, breakdown }: TrafficSourceTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('sessions')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpand = (key: string) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const { rows: channelData, unattributedConversions, totalConversions, hidden, hiddenSessions } = useMemo(() => {
    const entries = Object.entries(channels || {})
    const unattributed = channels?.[UNATTRIBUTED]?.conversions || 0
    const totalConv = entries.reduce((s, [, c]) => s + (c?.conversions || 0), 0)

    const all = entries.filter(([key]) => key !== UNATTRIBUTED)
    const real = all
      .map(([key, cell]) => {
        const sessions = cell?.sessions || 0
        const revenue = cell?.revenue || 0
        // Engagement gets its OWN denominator (see ChannelCell.engagement_sessions). Falling
        // back to `sessions` is right for a single-day caller, where the two coincide.
        const engN = cell?.engagement_sessions ?? sessions
        const measured = cell?.engaged != null && engN > 0
        return {
          name: CHANNEL_LABELS[key] || key,
          channelKey: key,
          sessions,
          conversions: cell?.conversions || 0,
          coverage: sessions ? (cell.sessions_with_origin || 0) / sessions : 0,
          ga4Sessions: ga4ChannelTotals?.[key]?.sessions ?? null,
          revenue,
          valuePerVisit: sessions > 0 ? revenue / sessions : 0,
          engagementRate: measured ? (cell.engaged as number) / engN : null,
          shopperRate: measured && cell?.shoppers != null ? cell.shoppers / engN : null,
          engagementSessions: measured ? engN : 0,
        }
      })
      .filter(r => r.sessions >= MIN_SESSIONS)

    // Sessions-weighted average CR across shown channels (the "vs promedio" base)
    const totalSessions = real.reduce((s, r) => s + r.sessions, 0)
    const totalRealConv = real.reduce((s, r) => s + r.conversions, 0)
    const avgRate = totalSessions > 0 ? (totalRealConv / totalSessions) * 100 : 0

    const rows: ChannelRow[] = real.map(r => {
      const rate = r.sessions > 0 ? (r.conversions / r.sessions) * 100 : 0
      return {
        ...r,
        conversionRate: rate,
        vsAverage: avgRate > 0 ? rate / avgRate : 1,
      }
    })
    // Channels below the noise floor are hidden, but SAID — a channel with traffic
    // disappearing without a word is the same failure as omitting it from the map.
    const hidden = all.length - real.length
    const hiddenSessions = all.reduce((s, [, c]) => s + (c?.sessions || 0), 0) - totalSessions
    return {
      rows,
      unattributedConversions: unattributed,
      totalConversions: totalConv,
      hidden,
      hiddenSessions,
    }
  }, [channels, ga4ChannelTotals])

  const sourceData = channelData
  const hasGA4 = !!ga4ChannelTotals && Object.keys(ga4ChannelTotals).length > 0
  const coveragePct = coverage && coverage.sessions_total > 0
    ? Math.round((coverage.sessions_with_origin / coverage.sessions_total) * 100)
    : null
  const unattributedPct = totalConversions > 0
    ? Math.round((unattributedConversions / totalConversions) * 100)
    : 0

  const sortedData = useMemo(() => {
    return [...sourceData].sort((a, b) => {
      const aVal = a[sortKey]
      const bVal = b[sortKey]
      // An unmeasured rate is not "zero" — it sorts last in both directions rather than
      // pretending to be the worst value.
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1
      const multiplier = sortDirection === 'desc' ? -1 : 1
      return (aVal - bVal) * multiplier
    })
  }, [sourceData, sortKey, sortDirection])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(key)
      setSortDirection('desc')
    }
  }

  const getVsAverageDisplay = (vsAvg: number): { icon: JSX.Element; text: string; appearance: 'success' | 'danger' | 'neutral' } => {
    if (vsAvg >= 1.2) {
      return {
        icon: <Icon source={<ArrowupIcon />} color="currentColor" />,
        text: `${vsAvg.toFixed(1)}x`,
        appearance: 'success',
      }
    } else if (vsAvg <= 0.8) {
      return {
        icon: <Icon source={<ArrowDownIcon />} color="currentColor" />,
        text: `${vsAvg.toFixed(1)}x`,
        appearance: 'danger',
      }
    }
    return {
      icon: <Icon source={<ArrowsHorizontalIcon />} color="currentColor" />,
      text: `${vsAvg.toFixed(1)}x`,
      appearance: 'neutral',
    }
  }

  const SortableHeader = ({ label, sortKeyValue }: { label: string; sortKeyValue: SortKey }) => (
    <button
      onClick={() => toggleSort(sortKeyValue)}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {label}
      <span className={sortKey === sortKeyValue ? 'text-primary' : 'text-muted-foreground/50'}>
        <Icon source={<ArrowsVerticalIcon />} color="currentColor" />
      </span>
    </button>
  )

  const header = (
    <Card.Header>
      <div className="flex items-start justify-between gap-2">
        <div>
          <Title as="h3">Rendimiento por Canal de Tráfico</Title>
          <Text>Cómo agrupa GA4 el origen de tus visitantes</Text>
        </div>
        {coveragePct !== null && (
          <div className="shrink-0 whitespace-nowrap" title="Sesiones con origen capturado por TNX (el resto usa el referente). Mayor cobertura = atribución más confiable.">
            <Tag appearance="neutral">
              Origen en {coveragePct}% de sesiones
            </Tag>
          </div>
        )}
      </div>
    </Card.Header>
  )

  if (sourceData.length === 0) {
    return (
      <Card>
        {header}
        <Card.Body>
          <div className="flex items-center justify-center py-8">
            <Text>No hay suficientes datos de canales de tráfico disponibles.</Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      {header}
      <Card.Body>
        <Table minWidth="1040px" stickyScrollbar>
          <Table.Head>
            <Table.Row>
              <Table.Cell as="th" className="w-[160px]">Canal</Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Sesiones" sortKeyValue="sessions" />
              </Table.Cell>
              {hasGA4 && <Table.Cell as="th" className="text-right text-muted-foreground">GA4</Table.Cell>}
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Conv." sortKeyValue="conversions" />
              </Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Tasa (≥)" sortKeyValue="conversionRate" />
              </Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Ingresos" sortKeyValue="revenue" />
              </Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="$/visita" sortKeyValue="valuePerVisit" />
              </Table.Cell>
              {/* The merchant surface's X axis, finally readable here too. `shoppers` rides in
                  the cell's tooltip rather than a tenth column — it's a secondary number,
                  confounded by landing-page choice, and never a yardstick on its own. */}
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="Engagement" sortKeyValue="engagementRate" />
              </Table.Cell>
              <Table.Cell as="th" className="text-right">
                <SortableHeader label="vs Promedio" sortKeyValue="vsAverage" />
              </Table.Cell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {sortedData.map((row) => {
              const vsDisplay = getVsAverageDisplay(row.vsAverage)
              const ga4Title = row.ga4Sessions != null
                ? `GA4: ${row.ga4Sessions.toLocaleString()} · TNX captó ${row.ga4Sessions > 0 ? Math.round((row.sessions / row.ga4Sessions) * 100) : 0}% · cobertura origen ${Math.round(row.coverage * 100)}%`
                : 'Sin referencia GA4 para este canal'
              const bd = breakdown?.[row.channelKey]
              // Drill cells now carry the channel's full metric set (reader `aggregateBreakdown`),
              // so a campaign can be read on the same four numbers as the channel above it.
              const drill = (label: string, c: BreakdownCell, medium: string | null, muted: boolean) => ({
                label,
                medium,
                muted,
                sessions: c.sessions,
                conversions: c.conversions,
                revenue: c.revenue ?? null,
                engaged: c.engaged ?? null,
                shoppers: c.shoppers ?? null,
              })
              const drillRows = bd
                ? [
                    ...bd.campaigns.map(c => drill(c.campaign, c, c.medium, false)),
                    ...(bd.untagged.sessions > 0 ? [drill('Sin campaña', bd.untagged, null, true)] : []),
                    ...(bd.other.sessions > 0 ? [drill('Otras campañas', bd.other, null, true)] : []),
                  ]
                : []
              // Residual = channel total (full window) − what the breakdown covers
              // (campaign data only exists for recent days). Surfaced so the rows reconcile
              // to the channel total instead of silently under-summing. Its revenue/engagement
              // stay null: this row is defined by what we DON'T have per campaign.
              if (bd) {
                const bdS = drillRows.reduce((s, d) => s + d.sessions, 0)
                const bdC = drillRows.reduce((s, d) => s + d.conversions, 0)
                const resS = row.sessions - bdS
                const resC = row.conversions - bdC
                if (resS > 0) {
                  drillRows.push({
                    label: 'Sin datos de campaña', medium: null, muted: true,
                    sessions: resS, conversions: Math.max(0, resC),
                    revenue: null, engaged: null, shoppers: null,
                  })
                }
              }
              const canExpand = drillRows.length > 0
              const isOpen = expanded.has(row.channelKey)
              return (
                <Fragment key={row.channelKey}>
                  <Table.Row className={canExpand ? 'cursor-pointer' : ''} onClick={canExpand ? () => toggleExpand(row.channelKey) : undefined}>
                    <Table.Cell className="font-medium">
                      <span className="flex items-center gap-1">
                        {canExpand
                          ? (isOpen ? <Icon source={<ChevronDownIcon />} /> : <Icon source={<ChevronRightIcon />} />)
                          : <span className="w-3.5" />}
                        {row.name}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="text-right font-mono">{row.sessions.toLocaleString()}</Table.Cell>
                    {hasGA4 && (
                      <Table.Cell className="text-right font-mono text-muted-foreground text-xs" title={ga4Title}>
                        {row.ga4Sessions != null ? row.ga4Sessions.toLocaleString() : '—'}
                      </Table.Cell>
                    )}
                    <Table.Cell className="text-right font-mono">{row.conversions.toLocaleString()}</Table.Cell>
                    <Table.Cell className="text-right font-mono">≥ {row.conversionRate.toFixed(2)}%</Table.Cell>
                    <Table.Cell className="text-right font-mono">{fmtMoney(row.revenue)}</Table.Cell>
                    <Table.Cell className="text-right font-mono">{fmtMoney(row.valuePerVisit)}</Table.Cell>
                    <Table.Cell
                      className="text-right font-mono"
                      title={
                        row.engagementRate == null
                          ? 'Sin datos de engagement en esta ventana'
                          : `${fmtRate(row.shopperRate)} vieron producto o categoría · medido sobre ${row.engagementSessions.toLocaleString()} sesiones`
                      }
                    >
                      {fmtRate(row.engagementRate)}
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      <Tag appearance={vsDisplay.appearance}>
                        {vsDisplay.icon}
                        {vsDisplay.text}
                      </Tag>
                    </Table.Cell>
                  </Table.Row>
                  {isOpen && drillRows.map((d, i) => {
                    const rate = d.sessions > 0 ? (d.conversions / d.sessions) * 100 : 0
                    // A campaign's engagement denominator IS its own sessions: the cell either
                    // carried `engaged` or it didn't, and an absent counter renders as «—».
                    const engRate = d.engaged != null && d.sessions > 0 ? d.engaged / d.sessions : null
                    const shopRate = d.shoppers != null && d.sessions > 0 ? d.shoppers / d.sessions : null
                    return (
                      <Table.Row key={`${row.channelKey}:${d.label}:${i}`} className="bg-muted/30 text-sm">
                        <Table.Cell className={`pl-8 ${d.muted ? 'italic text-muted-foreground' : ''}`}>
                          {d.label}
                          {d.medium ? <span className="ml-1 text-xs text-muted-foreground">· {d.medium}</span> : null}
                          {/* Real campaigns only — the untagged / folded / residual bags name
                              nothing you could look up in an ads manager. */}
                          {!d.muted && <CopyCampaign value={d.label} />}
                        </Table.Cell>
                        <Table.Cell className="text-right font-mono">{d.sessions.toLocaleString()}</Table.Cell>
                        {hasGA4 && <Table.Cell className="text-right font-mono">—</Table.Cell>}
                        <Table.Cell className="text-right font-mono">{d.conversions.toLocaleString()}</Table.Cell>
                        <Table.Cell className="text-right font-mono">≥ {rate.toFixed(2)}%</Table.Cell>
                        <Table.Cell className="text-right font-mono">
                          {d.revenue == null ? '—' : fmtMoney(d.revenue)}
                        </Table.Cell>
                        <Table.Cell className="text-right font-mono">
                          {d.revenue == null || d.sessions === 0 ? '—' : fmtMoney(d.revenue / d.sessions)}
                        </Table.Cell>
                        <Table.Cell
                          className="text-right font-mono"
                          title={shopRate == null ? '' : `${fmtRate(shopRate)} vieron producto o categoría`}
                        >
                          {fmtRate(engRate)}
                        </Table.Cell>
                        <Table.Cell className="text-right font-mono">—</Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Fragment>
              )
            })}
            {unattributedConversions > 0 && (
              <Table.Row className="text-muted-foreground">
                <Table.Cell className="font-medium italic">Sin atribuir</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
                {hasGA4 && <Table.Cell className="text-right font-mono">—</Table.Cell>}
                <Table.Cell className="text-right font-mono">{unattributedConversions.toLocaleString()}</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
                <Table.Cell className="text-right font-mono">—</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        <div className="mt-4 flex flex-col gap-1">
          <Text fontSize="caption">
            Tasa de conversión por canal es un <strong>piso</strong> (≥): {unattributedPct}% de las conversiones no
            pudieron atribuirse a ningún canal y se muestran como “Sin atribuir”. No compares tasas entre canales como
            valores exactos.
          </Text>
          <Text fontSize="caption">
            <strong>Engagement</strong> = sesiones que hicieron algo más que rebotar (ga4-engagement-v1). Es el eje
            horizontal de «Tu tráfico», así que esta tabla y ese gráfico se leen con la misma vara. «—» significa que
            la ventana no midió engagement para esa fila, no que sea cero.
          </Text>
          {hidden > 0 && (
            <Text fontSize="caption">
              {hidden} {hidden === 1 ? 'canal' : 'canales'} con menos de {MIN_SESSIONS} sesiones{' '}
              {hidden === 1 ? 'quedó' : 'quedaron'} fuera de la tabla ({hiddenSessions.toLocaleString()}{' '}
              {hiddenSessions === 1 ? 'sesión' : 'sesiones'} en total).
            </Text>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}
